import {
	AddToCartSnackbar,
	CampaignButton,
	CartButton,
	CartChip,
	PageHeaderButtons,
	PageHeaderContainer,
	ShoppingCartButton
} from "features/MarketingMaterials/pages/components/PageHeader/PageHeader.styles.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { MarketingMaterialsCart } from "features/MarketingMaterials/hooks/useMarketingMaterialsCart.ts";
import Chip from "@mui/material/Chip";
import { UserRole } from "types/declarations/enums.ts";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { useEffect } from "react";
import { marketingMaterialsCartActions } from "features/reducers/marketingMaterials/cart.ts";
import { getMarketingMaterialsLandingUrl } from "features/MarketingMaterials/utils/getMarketingMaterialsLandingUrl.ts";

export interface PageHeaderProps {
	title?: string;
	cartData?: MarketingMaterialsCart;
}

export const PageHeader = ({ title = "Marketing Materials", cartData }: PageHeaderProps) => {
	const { data: user } = useGetUserDetailsQuery();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const addedItemName = useSelector((state: RootState) => state.marketingMaterialsCart.addedItemName);
	const isAddToCartSnackbarOpen = useSelector(
		(state: RootState) => state.marketingMaterialsCart.isAddToCartSnackbarOpen
	);

	useEffect(() => {
		if (!isAddToCartSnackbarOpen) {
			return;
		}

		const timer = window.setTimeout(() => {
			dispatch(marketingMaterialsCartActions.hideAddToCartSnackbar());
		}, 2000);

		return () => window.clearTimeout(timer);
	}, [dispatch, isAddToCartSnackbarOpen]);

	const viewCart = () => {
		navigate("/marketing-materials/view-cart");
	};

	const goToMarketingMaterialsLandingPage = () => {
		navigate(getMarketingMaterialsLandingUrl());
	};

	return (
		<PageHeaderContainer
			data-testid="marketing-materials-page-header"
			isSnackbarOpen={isAddToCartSnackbarOpen}
		>
			<div
				onClick={() => goToMarketingMaterialsLandingPage()}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						goToMarketingMaterialsLandingPage();
					}
				}}
				role="button"
				tabIndex={0}
				data-testid="marketing-materials-title-link"
			>
				<h1>{title}</h1>
			</div>
			<PageHeaderButtons>
				<CartButton onClick={viewCart}>
					<CartChip label={cartData?.cartSize} />
					<ShoppingCartButton fontSize="large" />
				</CartButton>
				{user?.role === UserRole.SALES && (
					<CartButton>
						<Chip
							label={cartData?.campaignItems.length}
							color="warning"
						/>
						<CampaignButton fontSize="large" />
					</CartButton>
				)}
			</PageHeaderButtons>
			<AddToCartSnackbar
				open={isAddToCartSnackbarOpen}
				message={`${addedItemName ?? "Item"} added to cart`}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				isSales={user?.role === UserRole.SALES}
			/>
		</PageHeaderContainer>
	);
};
